import { useState, useEffect } from "react";
import { useForm, useStore } from "@tanstack/react-form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FormattedNumberInput } from "@/components/shared/FormattedNumberInput";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Save, Loader2, RefreshCcw } from "lucide-react";
import { SharedCombobox } from "@/components/shared/SharedCombobox";
import { useProcessLookup } from "@/hooks/useFKLookup";
import { trpcClient } from "@/lib/trpc";
import { toast } from "sonner";
import { focusFirstError } from "@/lib/form/formUtils";
import { cn } from "@/lib/utils";
import {
  TanstackFieldWrapper,
  TanstackFormControl,
} from "@/components/ui/tanstack-form";
import { Badge } from "@/components/ui/badge";
import { useRoleAccess } from "@/hooks/useRoleAccess";
import {
  technicalSpecFormSchema,
  type TechnicalSpecFormData,
} from "@/shared/schemas/tech.schema";
export type { TechnicalSpecFormData };

export interface ProcessStepDetail {
  method: string;
  standard: string;
  note: string;
}

interface TechnicalSpecFormProps {
  defaultValues?: Partial<TechnicalSpecFormData>;
  onSubmit: (data: TechnicalSpecFormData) => Promise<void>;
  onCancel: () => void;
  isSubmitting?: boolean;
  submitLabel?: string;
}

export default function TechnicalSpecForm({
  defaultValues,
  onSubmit,
  onCancel,
  isSubmitting,
  submitLabel = "Lưu",
}: TechnicalSpecFormProps) {
  const [customerId, setCustomerId] = useState(defaultValues?.customerId || "");
  const [availableProcessSteps, setAvailableProcessSteps] = useState<string[]>(
    [],
  );

  // Quick Add States
        const [quickAddSearch, setQuickAddSearch] = useState("");
  const [moldTargetField, setMoldTargetField] = useState<
    "dieCutId" | "foilDieId" | "screenId" | null
  >(null);

  const form = useForm({
    defaultValues: {
      productId: defaultValues?.productId ?? "",
      productName: defaultValues?.productName ?? "",
      status: defaultValues?.status ?? "DRAFT",
      version: defaultValues?.version ?? "1.0",
      notes: defaultValues?.notes ?? "",

      materialId: defaultValues?.materialId ?? "",
      packingSpec: defaultValues?.packingSpec ?? "",
      process: defaultValues?.process ?? "",

      layoutId: defaultValues?.layoutId ?? "",
      dieCutId: defaultValues?.dieCutId ?? "",
      foilDieId: defaultValues?.foilDieId ?? "",
      screenId: defaultValues?.screenId ?? "",
      designFile: defaultValues?.designFile ?? "",
      designStatus: defaultValues?.designStatus ?? "PENDING",
      designNotes: defaultValues?.designNotes ?? "",

      specDetails: {
        printSize: defaultValues?.specDetails?.printSize ?? "",
        ups: defaultValues?.specDetails?.ups ?? "",
        grainDirection: defaultValues?.specDetails?.grainDirection ?? "",
        finishedSizeLength:
          defaultValues?.specDetails?.finishedSizeLength ?? "",
        finishedSizeWidth: defaultValues?.specDetails?.finishedSizeWidth ?? "",
        finishedSizeHeight:
          defaultValues?.specDetails?.finishedSizeHeight ?? "",
        steps: defaultValues?.specDetails?.steps ?? {},
      },
    },
    validators: {
      onChange: technicalSpecFormSchema as any,
    },
    onSubmit: async ({ value }) => {
      await onSubmit(value as TechnicalSpecFormData);
    },
  });

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    form.handleSubmit();
    requestAnimationFrame(() => focusFirstError(form.state as any));
  };

  useEffect(() => {
    if (defaultValues?.process) {
      const steps = defaultValues.process
        .split(",")
        .map((s: string) => s.trim())
        .filter(Boolean);
      setAvailableProcessSteps(steps);
    }
  }, [defaultValues?.process]);

  useEffect(() => {
    if (defaultValues?.customerId && !customerId) {
      setCustomerId(defaultValues.customerId);
    }
  }, [defaultValues?.customerId, customerId]);

  const processLookup = useProcessLookup();
  const { hasPermission } = useRoleAccess();

  const handleAutoSyncFromProduct = async () => {
    const pId = form.getFieldValue("productId");
    if (!pId) {
      toast.error("Vui lòng chọn Sản phẩm trước để lấy dữ liệu.");
      return;
    }
    try {
      const toastId = toast.loading("Đang đồng bộ dữ liệu từ Sản phẩm...");
      const prod = await trpcClient.products.getById.query({ id: pId });

      if (prod.materialId) form.setFieldValue("materialId", prod.materialId);
      if (prod.packingSpec) form.setFieldValue("packingSpec", prod.packingSpec);
      if (prod.process) {
        form.setFieldValue("process", prod.process);
        const steps = prod.process
          .split(",")
          .map((s: string) => s.trim())
          .filter(Boolean);
        setAvailableProcessSteps(steps);
      }
      if (prod.notes) form.setFieldValue("notes", prod.notes);
    } catch (e: any) {
      toast.error("Không thể đồng bộ: " + e?.message);
    }
  };

  const F = ({
    name,
    label,
    required,
    placeholder,
    span = 1,
    type = "text",
    addon,
  }: any) => (
    <form.Field name={name}>
      {(field: any) => (
        <div
          className={cn(
            span === 2 && "col-span-2",
            span === 3 && "col-span-2 md:col-span-3",
            span === 4 && "col-span-2 md:col-span-4",
          )}
        >
          <TanstackFieldWrapper field={field} label={label} required={required}>
            <TanstackFormControl>
              <div className="relative">
                {type === "number" ? (
                  <FormattedNumberInput
                    id={field.name}
                    value={field.state.value}
                    placeholder={placeholder}
                    className={cn("h-9", addon && "pr-8")}
                    onValueChange={(val) => field.handleChange(val)}
                    onBlur={field.handleBlur}
                  />
                ) : (
                  <Input
                    id={field.name}
                    type={type}
                    value={field.state.value ?? ""}
                    placeholder={placeholder}
                    className={cn("h-9", addon && "pr-8")}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                  />
                )}
                {addon && (
                  <span className="absolute right-3 top-2 text-xs text-slate-400">
                    {addon}
                  </span>
                )}
              </div>
            </TanstackFormControl>
          </TanstackFieldWrapper>
        </div>
      )}
    </form.Field>
  );

  const SH = ({ label, desc }: { label: string; desc?: string }) => (
    <div className="mb-3 border-b pb-2">
      <h3 className="text-sm font-bold text-indigo-700 uppercase tracking-wider">
        {label}
      </h3>
      {desc && <p className="text-xs text-slate-500 mt-0.5">{desc}</p>}
    </div>
  );

  // Watch process to render dynamic steps
  const currentProcess = useStore(
    form.store,
    (state: any) => state.values.process,
  );
  const activeSteps = currentProcess
    ? currentProcess
        .split(",")
        .map((s: string) => s.trim())
        .filter(Boolean)
    : [];

  return (
    <>
      <form onSubmit={handleFormSubmit} className="space-y-6 pb-10">
        {/* ── KHỐI 1: QUẢN LÝ TSKT ── */}
        <div className="border rounded-xl bg-white shadow-sm p-5">
          <div className="flex justify-between items-end mb-4">
            <SH
              label="1. Quản lý TSKT & Liên kết"
              desc="Lọc Khách hàng trước để chọn Sản phẩm nhanh hơn"
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 text-xs text-indigo-600 border-indigo-200"
              onClick={handleAutoSyncFromProduct}
            >
              <RefreshCcw className="w-3.5 h-3.5 mr-1.5" /> Lấy data từ Product
            </Button>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-5 gap-y-4">
            <div className="col-span-2 space-y-1">
              <SharedCombobox
                type="customer"
                value={customerId}
                onChange={(id) => setCustomerId(id)}
                label="Lọc theo Khách hàng (Tùy chọn)"
                placeholder="Chọn Khách hàng..."
                canAdd={hasPermission("customers", "create")}
                onQuickAddClick={(search) => {
                  setQuickAddSearch(search);
                }}
              />
            </div>
            <form.Field name="productId">
              {(field: any) => (
                <div className="col-span-2 space-y-1">
                  <TanstackFieldWrapper field={field} label="Sản phẩm" required>
                    <TanstackFormControl>
                      <SharedCombobox
                        type="product"
                        params={customerId ? { customerId } : undefined}
                        value={field.state.value || ""}
                        onChange={(id, opt) => {
                          field.handleChange(id);
                          if (opt?.original?.customerId) {
                            setCustomerId(opt.original.customerId);
                          }
                          if (opt?.label) {
                            form.setFieldValue("productName", opt.label);
                          }
                        }}
                        placeholder="Chọn Sản phẩm..."
                        canAdd={hasPermission("products", "create")}
                        onQuickAddClick={(search) => {
                          setQuickAddSearch(search);
                        }}
                      />
                    </TanstackFormControl>
                  </TanstackFieldWrapper>
                </div>
              )}
            </form.Field>
            <F
              name="version"
              label="Phiên bản"
              placeholder="VD: 1.0"
              required
            />
          </div>
        </div>

        {/* ── KHỐI 2: THIẾT KẾ & ARTWORK ── */}
        <div className="border rounded-xl bg-white shadow-sm p-5">
          <SH
            label="2. Trạng thái Thiết kế & Artwork"
            desc="Theo dõi trạng thái file thiết kế từ khách hàng hoặc nội bộ"
          />
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-5 gap-y-4">
            <form.Field name="designStatus">
              {(field: any) => (
                <div className="col-span-2 space-y-1">
                  <TanstackFieldWrapper
                    field={field}
                    label="Trạng thái Thiết kế"
                  >
                    <TanstackFormControl>
                      <Select
                        value={field.state.value}
                        onValueChange={(val) => field.handleChange(val)}
                      >
                        <SelectTrigger className="h-9">
                          <SelectValue placeholder="Chọn trạng thái..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="PENDING">
                            Chờ thiết kế/Artwork
                          </SelectItem>
                          <SelectItem value="IN_PROGRESS">
                            Đang thiết kế
                          </SelectItem>
                          <SelectItem value="COMPLETED">
                            Đã hoàn thành
                          </SelectItem>
                          <SelectItem value="APPROVED">
                            Đã duyệt (Khách OK)
                          </SelectItem>
                          <SelectItem value="REJECTED">
                            Bị từ chối/Sửa lại
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </TanstackFormControl>
                  </TanstackFieldWrapper>
                </div>
              )}
            </form.Field>

            <div className="col-span-2 md:col-span-4">
              <F
                name="designFile"
                label="File Thiết kế (URL)"
                placeholder="Nhập đường dẫn Drive/File thiết kế..."
                span={4}
              />
            </div>

            <form.Field name="designNotes">
              {(field: any) => (
                <div className="col-span-2 md:col-span-4 space-y-1">
                  <TanstackFieldWrapper field={field} label="Ghi chú Thiết kế">
                    <TanstackFormControl>
                      <Textarea
                        value={field.state.value ?? ""}
                        rows={2}
                        placeholder="Yêu cầu sửa chữa, lưu ý thiết kế, yêu cầu màu sắc..."
                        onChange={(e) => field.handleChange(e.target.value)}
                      />
                    </TanstackFormControl>
                  </TanstackFieldWrapper>
                </div>
              )}
            </form.Field>
          </div>
        </div>

        {/* ── KHỐI 3: THÔNG SỐ CHUNG & VẬT TƯ (Two-way Sync) ── */}
        <div className="border rounded-xl bg-white shadow-sm p-5">
          <SH
            label="3. Thông số Chung & Vật tư"
            desc="Dữ liệu tại đây sẽ tự động cập nhật ngược lại vào Sản phẩm gốc"
          />
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-5 gap-y-4">
            <form.Field name="materialId">
              {(field: any) => (
                <div className="col-span-2 space-y-1">
                  <TanstackFieldWrapper field={field} label="Chất liệu giấy">
                    <TanstackFormControl>
                      <SharedCombobox
                        type="material"
                        value={field.state.value || ""}
                        onChange={(id) => field.handleChange(id)}
                        placeholder="Chọn Mã vật tư giấy..."
                      />
                    </TanstackFormControl>
                  </TanstackFieldWrapper>
                </div>
              )}
            </form.Field>
            <F
              name="specDetails.printSize"
              label="Khổ giấy in"
              placeholder="D x R"
              addon="mm"
            />
            <F
              name="specDetails.ups"
              label="Số bát (Ups)"
              type="number"
              placeholder="Bao nhiêu con/tờ"
            />

            <F
              name="specDetails.grainDirection"
              label="Sớ giấy / Chiều sóng"
              placeholder="Sớ xuôi, Sớ ngang, Sóng E..."
              span={2}
            />
            <F
              name="packingSpec"
              label="Quy cách đóng gói"
              placeholder="VD: 100 hộp/thùng"
              span={2}
            />

            <div className="col-span-2 md:col-span-4 grid grid-cols-1 sm:grid-cols-3 gap-4 p-4 bg-slate-50 rounded-lg border border-slate-100">
              <div className="sm:col-span-3 pb-1 border-b text-xs font-bold text-slate-500 uppercase">
                Kích thước Thành phẩm
              </div>
              <F
                name="specDetails.finishedSizeLength"
                label="Chiều Dài (L)"
                addon="mm"
              />
              <F
                name="specDetails.finishedSizeWidth"
                label="Chiều Rộng (W)"
                addon="mm"
              />
              <F
                name="specDetails.finishedSizeHeight"
                label="Chiều Cao (H)"
                addon="mm"
              />
            </div>
          </div>
        </div>

        {/* ── KHỐI 4: CÔNG CỤ SẢN XUẤT ── */}
        <div className="border rounded-xl bg-white shadow-sm p-5">
          <SH
            label="4. Công cụ Sản xuất & Bình file"
            desc="Liên kết các công cụ phục vụ in ấn, bế dán"
          />
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-5 gap-y-4">
            <form.Field name="layoutId">
              {(field: any) => (
                <div className="col-span-2 space-y-1">
                  <TanstackFieldWrapper
                    field={field}
                    label="Bình file (Layout)"
                  >
                    <TanstackFormControl>
                      <SharedCombobox
                        type="layout"
                        value={field.state.value || ""}
                        onChange={(id) => field.handleChange(id)}
                        placeholder="Chọn Bình file..."
                      />
                    </TanstackFormControl>
                  </TanstackFieldWrapper>
                </div>
              )}
            </form.Field>
            <form.Field name="dieCutId">
              {(field: any) => (
                <div className="col-span-2 space-y-1">
                  <TanstackFieldWrapper field={field} label="Khuôn bế">
                    <TanstackFormControl>
                      <SharedCombobox
                        type="dieCut"
                        value={field.state.value || ""}
                        onChange={(id) => field.handleChange(id)}
                        placeholder="Chọn Khuôn bế..."
                        canAdd={hasPermission("molds", "create")}
                        quickAddLabel="Thêm mới Khuôn bế..."
                        onQuickAddClick={(search) => {
                          setQuickAddSearch(search);
                          setMoldTargetField("dieCutId");
                        }}
                      />
                    </TanstackFormControl>
                  </TanstackFieldWrapper>
                </div>
              )}
            </form.Field>
            <form.Field name="foilDieId">
              {(field: any) => (
                <div className="col-span-2 space-y-1">
                  <SharedCombobox
                    type="dieCut"
                    value={field.state.value || ""}
                    onChange={(id) => field.handleChange(id)}
                    label="Khuôn ép kim"
                    placeholder="Chọn Khuôn ép kim..."
                    canAdd={hasPermission("molds", "create")}
                    quickAddLabel="Thêm mới Khuôn ép kim..."
                    onQuickAddClick={(search) => {
                      setQuickAddSearch(search);
                      setMoldTargetField("foilDieId");
                    }}
                  />
                </div>
              )}
            </form.Field>
            <form.Field name="screenId">
              {(field: any) => (
                <div className="col-span-2 space-y-1">
                  <SharedCombobox
                    type="dieCut"
                    value={field.state.value || ""}
                    onChange={(id) => field.handleChange(id)}
                    label="Khuôn in lụa"
                    placeholder="Chọn Khuôn in lụa..."
                    canAdd={hasPermission("molds", "create")}
                    quickAddLabel="Thêm mới Khuôn in lụa..."
                    onQuickAddClick={(search) => {
                      setQuickAddSearch(search);
                      setMoldTargetField("screenId");
                    }}
                  />
                </div>
              )}
            </form.Field>
          </div>
        </div>

        {/* ── KHỐI 5: QUY TRÌNH SẢN XUẤT ĐỘNG (Dynamic Routing Table) ── */}
        <div className="border rounded-xl bg-white shadow-sm p-5 border-l-4 border-l-indigo-500">
          <SH
            label="5. Quy Trình Sản Xuất & Tiêu chuẩn chất lượng"
            desc="Mô tả kỹ thuật tự động sinh ra dựa theo Quy trình được chọn"
          />

          <form.Field name="process">
            {(field: any) => {
              const toggleProcess = (stepId: string) => {
                const current = field.state.value
                  ? field.state.value
                      .split(",")
                      .map((s: string) => s.trim())
                      .filter(Boolean)
                  : [];
                let next = [...current];
                if (next.includes(stepId)) {
                  next = next.filter((s: string) => s !== stepId);
                } else {
                  next.push(stepId);
                }
                // Sort based on original process lookup order to maintain routing flow
                const ordered = processLookup.options
                  .filter((o) => next.includes(o.original?.stepId || o.label))
                  .map((o) => o.original?.stepId || o.label);

                // Keep any custom steps that aren't in options
                const custom = next.filter(
                  (s: string) =>
                    !processLookup.options.some(
                      (o) => (o.original?.stepId || o.label) === s,
                    ),
                );

                field.handleChange([...ordered, ...custom].join(", "));
              };

              const availableBadges =
                availableProcessSteps.length > 0
                  ? processLookup.options.filter((opt) =>
                      availableProcessSteps.includes(
                        opt.original?.stepId || opt.label,
                      ),
                    )
                  : [];

              return (
                <div className="space-y-3 mb-6 bg-indigo-50/50 p-4 rounded-lg border border-indigo-100">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs font-bold text-indigo-800">
                      Cấu hình Routing (Quy trình chuẩn)
                    </Label>
                    {availableProcessSteps.length > 0 && (
                      <span className="text-[10px] text-slate-500">
                        Quy trình từ sản phẩm: {availableProcessSteps.length}{" "}
                        công đoạn
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {availableBadges.length > 0 ? (
                      availableBadges.map((opt) => {
                        const stepId = opt.original?.stepId || opt.label;
                        const isSelected = activeSteps.includes(stepId);
                        return (
                          <Badge
                            key={stepId}
                            variant={isSelected ? "default" : "outline"}
                            className={cn(
                              "cursor-pointer select-none py-1.5 px-3 transition-all",
                              isSelected
                                ? "bg-indigo-600 hover:bg-indigo-700"
                                : "bg-white hover:bg-slate-100",
                            )}
                            onClick={() => toggleProcess(stepId)}
                          >
                            {opt.label}
                          </Badge>
                        );
                      })
                    ) : (
                      <p className="text-xs text-slate-500 italic">
                        Hãy nhấn "Lấy data từ Product" để tải quy trình từ sản
                        phẩm
                      </p>
                    )}
                  </div>
                  <div className="pt-2">
                    <Input
                      value={field.state.value ?? ""}
                      onChange={(e) => field.handleChange(e.target.value)}
                      placeholder={
                        availableProcessSteps.length > 0
                          ? "Hoặc chỉnh sửa thủ công nếu cần..."
                          : "Nhập chuỗi quy trình: IN_OFFSET, BE, CAN_MANG..."
                      }
                      className="h-8 text-xs font-mono bg-white"
                    />
                    <p className="text-[10px] text-slate-500 mt-1">
                      Sắp xếp chuỗi công đoạn cách nhau bằng dấu phẩy. VD:
                      IN_OFFSET, BE_DEMI, DAN
                    </p>
                  </div>
                </div>
              );
            }}
          </form.Field>

          {/* Dynamic Table for Steps */}
          {activeSteps.length === 0 ? (
            <div className="text-center py-8 text-slate-400 text-sm border-2 border-dashed rounded-lg">
              Vui lòng chọn Quy trình sản xuất để hiển thị khung nhập liệu chi
              tiết.
            </div>
          ) : (
            <div className="border rounded-md overflow-x-auto bg-white">
              <table className="w-full text-sm text-left">
                <thead className="bg-slate-100 text-slate-600 text-xs uppercase border-b">
                  <tr>
                    <th className="px-3 py-2 w-[50px] text-center">Stt</th>
                    <th className="px-3 py-2 w-[180px]">Công đoạn</th>
                    <th className="px-3 py-2 w-[20%]">Phương pháp kiểm tra</th>
                    <th className="px-3 py-2 w-[30%]">Tiêu chuẩn</th>
                    <th className="px-3 py-2">Ghi chú</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {activeSteps.map((step: string, index: number) => {
                    const stepName =
                      processLookup.options.find(
                        (o) => (o.original?.stepId || o.label) === step,
                      )?.label || step;
                    return (
                      <tr key={step} className="hover:bg-slate-50/50 align-top">
                        <td className="px-3 py-3 text-center font-bold text-slate-400">
                          {index + 1}
                        </td>
                        <td className="px-3 py-3">
                          <div className="font-bold text-indigo-700">
                            {stepName}
                          </div>
                          <div className="text-[10px] font-mono text-slate-400 mt-1">
                            {step}
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          <form.Field name={`specDetails.steps.${step}.method`}>
                            {(field: any) => (
                              <Textarea
                                value={field.state.value ?? ""}
                                onChange={(e) =>
                                  field.handleChange(e.target.value)
                                }
                                className="min-h-[60px] h-full text-xs bg-white border-slate-200"
                                placeholder="PP Kiểm tra..."
                              />
                            )}
                          </form.Field>
                        </td>
                        <td className="px-3 py-2">
                          <form.Field
                            name={`specDetails.steps.${step}.standard`}
                          >
                            {(field: any) => (
                              <Textarea
                                value={field.state.value ?? ""}
                                onChange={(e) =>
                                  field.handleChange(e.target.value)
                                }
                                className="min-h-[60px] h-full text-xs bg-white border-slate-200"
                                placeholder="Tiêu chuẩn..."
                              />
                            )}
                          </form.Field>
                        </td>
                        <td className="px-3 py-2">
                          <form.Field name={`specDetails.steps.${step}.note`}>
                            {(field: any) => (
                              <Textarea
                                value={field.state.value ?? ""}
                                onChange={(e) =>
                                  field.handleChange(e.target.value)
                                }
                                className="min-h-[60px] h-full text-xs bg-white border-slate-200"
                                placeholder="Ghi chú thêm..."
                              />
                            )}
                          </form.Field>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* ── KHỐI 6: GHI CHÚ CHUNG ── */}
        <div className="border rounded-xl bg-white shadow-sm p-5">
          <SH label="6. Ghi chú chung & Lưu ý KCS" />
          <form.Field name="notes">
            {(field: any) => (
              <TanstackFieldWrapper field={field}>
                <TanstackFormControl>
                  <Textarea
                    value={field.state.value ?? ""}
                    rows={3}
                    placeholder="Các thông tin kỹ thuật đặc biệt, lưu ý đóng gói, yêu cầu ngoại lệ của Khách hàng..."
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                </TanstackFormControl>
              </TanstackFieldWrapper>
            )}
          </form.Field>
        </div>

        {/* ═══ Actions ═══ */}
        <div className="sticky bottom-0 z-50 -mx-4 sm:-mx-6 -mb-4 sm:-mb-6 backdrop-blur-md bg-white/80 p-4 border-t flex items-center justify-end gap-3 mt-6">
          <Button type="button" variant="outline" onClick={onCancel}>
            Huỷ
          </Button>
          <form.Subscribe selector={(s: any) => [s.canSubmit, s.isSubmitting]}>
            {([canSubmit, submitting]: any) => (
              <Button
                type="submit"
                disabled={!canSubmit || submitting || isSubmitting}
                className="min-w-[150px] shadow-md bg-indigo-600 hover:bg-indigo-700"
              >
                {submitting || isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Đang
                    lưu...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" /> {submitLabel}
                  </>
                )}
              </Button>
            )}
          </form.Subscribe>
        </div>
      </form>

      
      
          </>
  );
}
